import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'
import type { EmailLogDetail, EmailLogResponse, EmailPreview } from '@/types/emailLog'

export const emailLogKeys = {
  all: ['email-log'] as const,
  list: (params: Record<string, unknown>) => [...emailLogKeys.all, 'list', params] as const,
  detail: (id: string) => [...emailLogKeys.all, 'detail', id] as const,
  ticket: (ticketId: string) => [...emailLogKeys.all, 'ticket', ticketId] as const,
  preview: (id: string, images: boolean) => [...emailLogKeys.all, 'preview', id, images] as const,
}

// keepPreviousData: paging and filtering swap the rows in place instead of
// flashing the skeleton between pages.
export function useEmailLog(params: Record<string, unknown>) {
  return useQuery({
    queryKey: emailLogKeys.list(params),
    queryFn: () => api<EmailLogResponse>('/support-tickets/email-log', { query: params }),
    placeholderData: keepPreviousData,
  })
}

export function useEmailLogEntry(id: string | undefined) {
  return useQuery({
    queryKey: emailLogKeys.detail(id ?? ''),
    queryFn: () => api<EmailLogDetail>(`/support-tickets/email-log/${id}`),
    enabled: Boolean(id),
  })
}

export function useTicketEmails(ticketId: string, enabled: boolean) {
  return useQuery({
    queryKey: emailLogKeys.ticket(ticketId),
    queryFn: () => api<EmailLogResponse>(`/support-tickets/${ticketId}/emails`),
    enabled,
  })
}

export function useEmailPreview(id: string | undefined, images: boolean, enabled: boolean) {
  return useQuery({
    queryKey: emailLogKeys.preview(id ?? '', images),
    queryFn: () => api<EmailPreview>(`/support-tickets/email-log/${id}/preview`, { query: images ? { images: 1 } : undefined }),
    enabled: Boolean(id) && enabled,
    staleTime: 5 * 60_000,
  })
}
